import { Dropdown } from "@src/components/dropdown/Dropdown";
import { IModalHandle, Modal } from "@src/components/modal/Modal";
import { eventActions, pricingActions, settingsActions } from "@src/store";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { ArrowIcon } from "@src/assets/general-icons";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { IRowDataValueChangeProps } from "@src/components/vGrid/VGrid";
import React, { useRef, useState } from "react";
import styles from "./InventoryHeadline.module.scss";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import Highlight from "@src/components/highlight/Highlight";
import { ResetIconThemed } from "@src/assets/general-icons/ResetIconThemed";
import { MixpanelCategory, mixpanelTrack } from "@src/components/utils/mixPanelUtils";
import SectionHeadliner from "@src/components/sectionHeadliner/SectionHeadliner";
import { formatCurrency } from "@src/components/utils/numberUtils";
import LabelAndValue from "@src/components/labelAndValue/LabelAndValue";
import eventStyles from "../../events/Events.module.scss";
import _ from "lodash";
import { Switch } from "@src/components/switch/Switch";
import inventoryStyles from "../Inventory.module.scss";
import AutopricerIcon from "@src/assets/general-icons/AutopricerIcon";
import helperStyles from "@src/components/helpers.module.scss";
import CloudIcon from "@src/assets/general-icons/CloudIcon";
import { PenIcon } from "@src/assets/general-icons/PenIcon";
import MassPriceUpdate from "@src/components/massPriceUpdate/MassPriceUpdate";
import { LayoutActionEnum, ManageInventoryTabEnum } from "@src/store/pricing/selectionSlice";
import FullScreenThemed from "@src/assets/general-icons/FullScreenThemed";
import { MyTooltip } from "@src/components/tooltip/Tooltip";
import { IListingToUpdate } from "@src/store/pricing/updateListingPrices";
import { toast } from "sonner";
import DoubleCheckIcon from "@src/assets/general-icons/DoubleCheckIcon";
import { SumIcon } from "@src/assets/general-icons/SumIcon";
import { CommonModal } from "@src/components/modal/CommonModal";
import EventSummaryModal from "@src/pages/home/children/pricing/components/inventory/components/EventSummaryModal/EventSummaryModal";
import MergeListings from "@src/components/mergeListings/MergeListings";
import EditPriceModal from "@src/pages/home/children/pricing/components/inventory/components/EditPriceModal/EditPrice";
import EventSummary from "@src/components/eventSummary/EventSummary";
import DeleteListing from "@src/components/deleteListing/DeleteListing";
import { useSearchParams } from "react-router-dom";
import BulkSharing from "@src/components/bulkSharing/BulkSharing";
import BulkExternalNotes from "@src/components/bulkExternalNotes/BulkExternalNotes";
import BulkInternalNotes from "@src/components/bulkInternalNotes/BulkInternalNotes";
import BulkEditCost from "@src/components/bulkEditCost/BulkEditCost";
import BulkHoldListings from "@src/components/bulkHoldListings/BulkHoldListings";
import BulkInvoice from "@src/components/bulkInvoice/BulkInvoice";
import BulkEditSectionRow from "@src/components/bulkEditSectionRow/BulkEditSectionRow";
import BulkSchedulePrice from "@src/components/bulkSchedulePrice/BulkSchedulePrice";
import BulkMarkNotInHand from "@src/components/bulkMarkInHand/BulkMarkNotInHand";
import BulkActionsMenu from "@src/components/bulkActionsMenu/BulkActionsMenu";
import BulkTag from "@src/components/bulkTag/BulkTag";

interface IInventoryHeadline {
	data: Array<EventInventoryRecord>;
	changesMap: Map<string, IRowDataValueChangeProps<EventInventoryRecord>>;
	rowsData: Array<EventInventoryRecord>;
	showSold: boolean;
	showOnHold: boolean;
	groupInventory: boolean;
	handleChecked: () => void;
	handleOnHoldChecked: () => void;
	handleGroupInventoryChecked: () => void;
	fetchInventory: (skipCache?: boolean) => void;
	onDiscard?: () => Promise<void>;
	onApply?: () => Promise<void>;
}

export const InventoryHeadline: React.FC<IInventoryHeadline> = ({
	data,
	changesMap,
	rowsData,
	showSold,
	showOnHold,
	groupInventory,
	handleChecked,
	handleOnHoldChecked,
	handleGroupInventoryChecked,
	fetchInventory,
	onDiscard,
	onApply,
}) => {
	const [params, setParams] = useSearchParams();
	const eventInventoryAll = useAppSelector((state) => state.pricing.eventInventoryAll);
	const { inventoryItemIds, layoutAction, isEditListingEnabled, manageInventoryTab } = useAppSelector(
		(state) => state.pricing.selection,
	);
	const autoPricerButtonText = inventoryItemIds.length > 1 ? "Group Auto Pricer" : "Single Auto Pricer";
	const inventoryItems = _.filter(eventInventoryAll.data, (x) => inventoryItemIds.includes(x.Item_ID));
	const isInventoryFullScreen = layoutAction === LayoutActionEnum.InventoryFullScreen;
	const { data: eventSummary, loading } = useAppSelector((state) => state.pricing.eventSummary);
	const canShowEventSummary =
		(manageInventoryTab === ManageInventoryTabEnum.ManageListing && isEditListingEnabled) ||
		layoutAction === LayoutActionEnum.InventoryFullScreen;
	const { isAdmin, hasEditSeats } = useAppSelector((state) => state.settings);
	const canViewMergeListings = isAdmin || hasEditSeats;

	const massPriceUpdateRef = useRef<IModalHandle>(null);
	const mergeListingsRef = useRef<IModalHandle>(null);
	const editPriceRef = useRef<IModalHandle>(null);
	const deleteListingModalRef = useRef<IModalHandle>(null);
	const bulkSharingRef = useRef<IModalHandle>(null);
	const bulkExternalNotesRef = useRef<IModalHandle>(null);
	const bulkInternalNotesRef = useRef<IModalHandle>(null);
	const bulkEditCostRef = useRef<IModalHandle>(null);
	const bulkHoldListingsRef = useRef<IModalHandle>(null);
	const bulkInvoiceRef = useRef<IModalHandle>(null);
	const bulkEditSectionRowRef = useRef<IModalHandle>(null);
	const bulkSchedulePriceRef = useRef<IModalHandle>(null);
	const bulkMarkNotInHandRef = useRef<IModalHandle>(null);
	const bulkTagRef = useRef<IModalHandle>(null);

	const showUntaggedInventory = params.get("showUntaggedInventory") === "true";

	const [state, setState] = useState({
		isSaving: false,
	});

	const [_isReviewing, setIsReviewing] = useState(false);

	const dispatch = useAppDispatch();

	const openSharedAutopricerModal = () => {
		dispatch(eventActions.showComingSoon({ showComingSoon: true }));
	};

	const openAutopricerPopup = () => {
		dispatch(settingsActions.setAutoPricerPopup(true));
	};

	const openSingleAutopricer = () => {
		dispatch(eventActions.setInventory({ isEditListingEnabled: true }));
		dispatch(eventActions.setManageInventoryTab(ManageInventoryTabEnum.Autopricer));
	};

	const handleShowUntaggedInventoryToggle = () => {
		const newValue = !showUntaggedInventory;

		if (newValue) {
			params.set("showUntaggedInventory", String(newValue));
		} else {
			params.delete("showUntaggedInventory");
		}

		setParams(params);
	};

	const handleAutopricerOpen = () => {
		if (!inventoryItemIds.length) return;

		if (inventoryItemIds.length > 1) {
			dispatch(eventActions.showComingSoon({ showComingSoon: true }));
			return;
		}

		const isSelectedItemAutopriced = eventInventoryAll.data.find((x) => x.Item_ID === inventoryItemIds[0])?.Is_AutoPriced;

		if (isSelectedItemAutopriced) {
			openAutopricerPopup();
		} else {
			openSingleAutopricer();
		}

		mixpanelTrack(MixpanelCategory.AUTOPRICER_INVENTORY);
	};

	const openMassPriceUpdateModal = () => massPriceUpdateRef.current?.openModal();
	const closeMassPriceUpdateModal = () => massPriceUpdateRef.current?.closeModal();

	const openMergeListingsModal = () => mergeListingsRef.current?.openModal();
	const closeMergeListingsModal = () => mergeListingsRef.current?.closeModal();

	const openDeleteListingsModal = () => deleteListingModalRef.current?.openModal();
	const closeDeleteListingsModal = () => deleteListingModalRef.current?.closeModal();

	const openBulkSharingModal = () => bulkSharingRef.current?.openModal();
	const openBulkExternalNotesModal = () => bulkExternalNotesRef.current?.openModal();
	const openBulkInternalNotesModal = () => bulkInternalNotesRef.current?.openModal();
	const openBulkEditCostModal = () => bulkEditCostRef.current?.openModal();
	const openBulkHoldListingsModal = () => bulkHoldListingsRef.current?.openModal();
	const openBulkInvoiceModal = () => bulkInvoiceRef.current?.openModal();
	const openBulkEditSectionRowModal = () => bulkEditSectionRowRef.current?.openModal();
	const openBulkSchedulePriceModal = () => bulkSchedulePriceRef.current?.openModal();
	const openBulkMarkNotInHandModal = () => bulkMarkNotInHandRef.current?.openModal();
	const openBulkTagModal = () => bulkTagRef.current?.openModal();

	const onAutoPricerClick = () => {
		openSharedAutopricerModal();
	};

	const bulkActionItems = [
		{
			id: 1,
			label: "Auto Pricer",
			onClick: onAutoPricerClick,
			isHiden: false,
		},
		{ id: 2, label: "Mass Price Update", onClick: openMassPriceUpdateModal, isHiden: false },
		{ id: 3, label: "Merge Listings", onClick: openMergeListingsModal, isHiden: !canViewMergeListings },
		{ id: 4, label: "Bulk Sharing", onClick: openBulkSharingModal, isHiden: false },
		{ id: 5, label: "Bulk External Notes", onClick: openBulkExternalNotesModal, isHiden: false },
		{ id: 6, label: "Bulk Internal Notes", onClick: openBulkInternalNotesModal, isHiden: false },
		{ id: 7, label: "Bulk Edit Cost", onClick: openBulkEditCostModal, isHiden: false },
		{ id: 8, label: "Bulk Hold Listings", onClick: openBulkHoldListingsModal, isHiden: false },
		{ id: 9, label: "Bulk Invoice", onClick: openBulkInvoiceModal, isHiden: false },
		{ id: 10, label: "Edit Section/Row", onClick: openBulkEditSectionRowModal, isHiden: false },
		{ id: 11, label: "Bulk Schedule Pricing", onClick: openBulkSchedulePriceModal, isHiden: false },
		{ id: 12, label: "Bulk Tag", onClick: openBulkTagModal, isHiden: false },
	].filter((x) => !x.isHiden);

	const listings = rowsData.length;
	const quantity = _.sumBy(rowsData, (x) => x.Quantity);

	const cost = _.sumBy(rowsData, (x) => x.Item_Cost);
	const price = _.sumBy(rowsData, (x) => x.Item_Price * x.Quantity);

	const infoData = [
		...(groupInventory ? [{ id: 1, label: "Listings:", value: listings || "-" }] : []),
		{ id: 2, label: "Quantitiy:", value: quantity || "-" },
		{ id: 3, label: "Cost:", value: `${formatCurrency(cost, !!cost)}` },
		{ id: 4, label: "Price", value: `${formatCurrency(price, !!price)}` },
	];

	const mappedInfoData = infoData.map(({ id, label, value }) => {
		const color = id === 3 ? "var(--textDetails)" : undefined;
		return (
			<div key={id} className={eventStyles.headerInfoItem}>
				<LabelAndValue
					labelColor="secondary"
					labelStyle={{ fontSize: "1.2rem" }}
					valueStyle={{ fontSize: "2rem", color }}
					label={label}
					value={value}
				/>
			</div>
		);
	});

	const handleCustomize = () => {
		dispatch(settingsActions.setCustomizeTableTab("Inventory"));
		mixpanelTrack(MixpanelCategory.CUSTOMIZE_INVENTORY);
	};

	const handleInventoryExpansion = () => {
		dispatch(eventActions.setInventory({ layoutAction: LayoutActionEnum.InventoryFullScreen }));
	};

	const summaryModalRef = useRef<IModalHandle>(null);

	const cancelEditPriceModal = () => {
		editPriceRef.current?.closeModal();
		onDiscard?.();
	};

	const handleConfirmPriceChange = async () => {
		setState({ isSaving: true });
		try {
			await onApply?.();
			editPriceRef.current?.closeModal();
		} catch {
			toast.error("Failed to apply changes.");
		} finally {
			setState({ isSaving: false });
		}
	};

	const shouldShowEditPriceModal = (): boolean => {
		const suppressedUntil = localStorage.getItem("suppressEditPriceModalUntil");

		if (suppressedUntil) {
			const suppressedUntilTimestamp = parseInt(suppressedUntil, 10);
			if (Date.now() < suppressedUntilTimestamp) return false;
		}

		if (changesMap.size === 0) return false;

		const hasLargeChange = Array.from(changesMap.values()).some((change) => {
			const { originalValue, value } = change;

			if (originalValue == null || originalValue <= 0) return false;

			const percentageChange = Math.abs(value - originalValue) / originalValue;
			return percentageChange > 0.3;
		});

		return hasLargeChange;
	};

	const handleOpenEditPriceModal = async () => {
		if (shouldShowEditPriceModal()) {
			editPriceRef.current?.openModal();
		} else {
			await handleConfirmPriceChange();
		}
	};

	const handleEditPriceModalConfirm = async (dontShowAgain: boolean) => {
		if (dontShowAgain) {
			const suppressionUntil = new Date().getTime() + 60 * 60 * 1000;
			localStorage.setItem("suppressEditPriceModalUntil", suppressionUntil.toString());
		}

		await handleConfirmPriceChange();
	};

	const getReviewButtonState = () => {
		if (data.length === 0) {
			return {
				disabled: false,
				label: "Mark Event Reviewed",
				actionType: "event",
			};
		}

		const allSelected = data.length === rowsData.filter((x) => !x.Invoice_ID).length;

		return {
			disabled: false,
			label: allSelected ? "Mark Event Reviewed" : "Mark Tickets Reviewed",
			actionType: allSelected ? "event" : "tickets",
		};
	};

	const reviewState = getReviewButtonState();

	const handleReviewClick = async () => {
		const { actionType } = getReviewButtonState();
		if (!actionType) return;
		setIsReviewing(true);

		try {
			if (actionType === "event") {
				if (!rowsData.length) {
					toast.error("Missing event data");
				}
				const { Event_ID: eventID, Company_Name: companyName } = rowsData[0];
				const acceptEventPricesPayload = { eventID, companyName };
				await dispatch(pricingActions.acceptEventPrices(acceptEventPricesPayload));
			}

			if (actionType === "tickets") {
				const updateListingPricesPayload: Array<IListingToUpdate> = data.map((x) => ({
					listing_id: x.Listing_ID,
					price: x.Item_Price,
					face: x.Face,
				}));
				await dispatch(pricingActions.updateListingPrices(updateListingPricesPayload));
			}

			fetchInventory(true);
			dispatch(eventActions.setInventory({ inventoryItemIds: [] }));
		} catch {
			toast.error("Something went wrong.");
		} finally {
			setIsReviewing(false);
		}
	};

	return (
		<>
			<SectionHeadliner
				top={canShowEventSummary ? <EventSummary /> : null}
				topLeft={
					<div className={styles.headlineContainer}>
						<div className={styles.headline}>Event Inventory</div>
						<div style={{ display: "flex", gap: "5px", flexShrink: 0 }}>
							<Highlight isHighlighted={data.length > 1}>
								<BulkActionsMenu
									items={bulkActionItems}
									onMarkNotInHand={openBulkMarkNotInHandModal}
									isDisabled={data.length < 2 || data.some((x) => !!x.On_Hold)}
									onOpenClick={() => mixpanelTrack(MixpanelCategory.BULK_ACTIONS)}
								/>
							</Highlight>
							{changesMap.size > 0 && (
								<>
									<Button
										onClick={onDiscard}
										disabled={state.isSaving}
										style={{
											padding: "0px 9px",
											fontSize: "1.2rem",
											display: "flex",
											alignItems: "center",
											gap: "5px",
										}}
										variant={ButtonVariant.Secondary}>
										<ResetIconThemed /> Discard
									</Button>
									<Button
										isLoading={state.isSaving}
										onClick={handleOpenEditPriceModal}
										style={{
											padding: "0px 9px",
											fontSize: "1.2rem",
											display: "flex",
											alignItems: "center",
											gap: "5px",
										}}
										variant={ButtonVariant.Primary}>
										<ArrowIcon /> Apply
									</Button>
								</>
							)}
						</div>
					</div>
				}
				topRight={
					<div className={inventoryStyles.rightContent}>
						{inventoryItemIds.length === 1 ? (
							<Button
								disabled={inventoryItemIds.length !== 1}
								onClick={openDeleteListingsModal}
								variant={ButtonVariant.Secondary}>
								Delete Listing
							</Button>
						) : null}
						<Button variant={ButtonVariant.Secondary}>
							<Switch checked={showSold} onChange={handleChecked} label="Show Sold" />
						</Button>
						<Button variant={ButtonVariant.Secondary}>
							<Switch checked={showOnHold} onChange={handleOnHoldChecked} label="Show On Hold" />
						</Button>
						<Dropdown
							style={{ minHeight: 34 }}
							buttonText="More actions"
							items={[
								{
									id: 3,
									isDisabled: !inventoryItems.length,
									onClick: handleAutopricerOpen,
									label: (
										<div className={helperStyles.flexSmallGap}>
											{autoPricerButtonText}
											<AutopricerIcon />
										</div>
									),
								},
								{
									id: 4,
									isDisabled: reviewState.disabled,
									onClick: handleReviewClick,
									label: (
										<div className={helperStyles.flexSmallGap}>
											<DoubleCheckIcon />
											{reviewState.label}
										</div>
									),
								},
								{
									id: 5,
									onClick: () => summaryModalRef.current?.openModal(),
									label: (
										<div className={helperStyles.flexSmallGap}>
											<SumIcon />
											Event Summary
										</div>
									),
								},
								{
									id: 2,
									preventClose: true,
									label: (
										<Switch
											checked={showUntaggedInventory}
											onChange={handleShowUntaggedInventoryToggle}
											label="Show Untagged"
										/>
									),
								},
								{
									id: 7,
									preventClose: true,
									label: (
										<Switch checked={groupInventory} onChange={handleGroupInventoryChecked} label="Group Inventory" />
									),
								},
							]}
						/>
						<MyTooltip
							id="fetchDataTooltip"
							popup="Re-fetch data"
							popupStyle={{ fontSize: "1rem" }}
							element={
								<Button
									onClick={() => {
										fetchInventory(true);
										mixpanelTrack(MixpanelCategory.FETCH);
									}}
									variant={ButtonVariant.Secondary}>
									<div className={helperStyles.centerWithGap}>
										<CloudIcon />
									</div>
								</Button>
							}
						/>
						{/* <MyTooltip
							popup={data.length != 1 ? "Please select a single listing to configure Scheduled Pricing." : null}
							popupStyle={{ fontSize: "1rem", fontWeight: 600 }}
							id="schedulePricingModalTooltip"
							element={
								<>
									<Modal
										ref={schedulePricingModalRef}
										customButton={
											<Button
												onClick={() => schedulePricingModalRef.current?.openModal()}
												disabled={data.length != 1}
												variant={ButtonVariant.Secondary}>
												Scheduled Pricing
											</Button>
										}>
										<CommonModal
											width={600}
											closeModal={() => schedulePricingModalRef.current?.closeModal()}
											title="Schedule Price"
											content={
												<SchedulePrice
													tableView
													inventoryItem={data[0]}
													onSuccess={() => schedulePricingModalRef.current?.closeModal()}
												/>
											}
											buttons={
												<Button
													onClick={() => schedulePricingModalRef.current?.closeModal()}
													variant={ButtonVariant.Secondary}>
													Close
												</Button>
											}
										/>
									</Modal>
								</>
							}
						/> */}
						<MyTooltip
							id="customizeTooltip"
							popup="Customize"
							popupStyle={{ fontSize: "1rem" }}
							element={
								<Button onClick={handleCustomize} variant={ButtonVariant.Secondary}>
									<div className={helperStyles.centerWithGap}>
										<PenIcon />
									</div>
								</Button>
							}
						/>
						<MyTooltip
							id={`mappedResize-inventoryFullscreen`}
							popupStyle={{ fontSize: "1rem", fontWeight: 600 }}
							popup="Full Screen View – focus on listings and pricing."
							element={
								<Button
									data-is-active={isInventoryFullScreen}
									variant={ButtonVariant.Secondary}
									onClick={handleInventoryExpansion}>
									<FullScreenThemed />
								</Button>
							}
						/>
					</div>
				}
				bottom={mappedInfoData}
			/>
			<Modal ref={summaryModalRef}>
				<CommonModal
					width={550}
					closeModal={() => summaryModalRef.current?.closeModal()}
					title="Event Summary"
					content={<EventSummaryModal isLoading={loading} eventSummary={eventSummary} />}
				/>
			</Modal>
			<Modal ref={massPriceUpdateRef}>
				<MassPriceUpdate handleClose={closeMassPriceUpdateModal} />
			</Modal>
			<Modal ref={bulkSharingRef}>
				<CommonModal
					width="auto"
					padding="0px"
					closeModal={() => bulkSharingRef.current?.closeModal()}
					title="Bulk Sharing"
					content={<BulkSharing handleClose={() => bulkSharingRef.current?.closeModal()} />}
				/>
			</Modal>
			<Modal ref={bulkExternalNotesRef}>
				<CommonModal
					width="auto"
					padding="0px"
					closeModal={() => bulkExternalNotesRef.current?.closeModal()}
					title="External Notes"
					content={<BulkExternalNotes handleClose={() => bulkExternalNotesRef.current?.closeModal()} />}
				/>
			</Modal>
			<Modal ref={bulkInternalNotesRef}>
				<CommonModal
					width="auto"
					padding="0px"
					closeModal={() => bulkInternalNotesRef.current?.closeModal()}
					title="Internal Notes"
					content={<BulkInternalNotes handleClose={() => bulkInternalNotesRef.current?.closeModal()} />}
				/>
			</Modal>
			<Modal ref={bulkEditCostRef}>
				<CommonModal
					width="auto"
					padding="0px"
					closeModal={() => bulkEditCostRef.current?.closeModal()}
					title="Edit Cost"
					content={<BulkEditCost handleClose={() => bulkEditCostRef.current?.closeModal()} />}
				/>
			</Modal>
			<Modal ref={bulkHoldListingsRef}>
				<CommonModal
					width="auto"
					padding="0px"
					closeModal={() => bulkHoldListingsRef.current?.closeModal()}
					title="Hold listings"
					content={<BulkHoldListings handleClose={() => bulkHoldListingsRef.current?.closeModal()} />}
				/>
			</Modal>
			<Modal ref={bulkInvoiceRef}>
				<CommonModal
					width="auto"
					padding="0px"
					closeModal={() => bulkInvoiceRef.current?.closeModal()}
					title="Bulk Invoice"
					content={<BulkInvoice handleClose={() => bulkInvoiceRef.current?.closeModal()} />}
				/>
			</Modal>
			<Modal ref={bulkEditSectionRowRef}>
				<CommonModal
					width="auto"
					padding="0px"
					closeModal={() => bulkEditSectionRowRef.current?.closeModal()}
					title="Edit Section/Row"
					content={<BulkEditSectionRow handleClose={() => bulkEditSectionRowRef.current?.closeModal()} />}
				/>
			</Modal>
			<Modal ref={bulkSchedulePriceRef}>
				<CommonModal
					width="auto"
					padding="0px"
					closeModal={() => bulkSchedulePriceRef.current?.closeModal()}
					title="Schedule Pricing"
					content={<BulkSchedulePrice handleClose={() => bulkSchedulePriceRef.current?.closeModal()} />}
				/>
			</Modal>
			<Modal ref={bulkTagRef}>
				<CommonModal
					width="auto"
					padding="0px"
					closeModal={() => bulkTagRef.current?.closeModal()}
					title="Bulk Tag"
					content={<BulkTag handleClose={() => bulkTagRef.current?.closeModal()} />}
				/>
			</Modal>
			<Modal ref={bulkMarkNotInHandRef}>
				<CommonModal
					width={460}
					padding="0px"
					closeModal={() => bulkMarkNotInHandRef.current?.closeModal()}
					title="Mark Tickets Not In Hand"
					content={<BulkMarkNotInHand handleClose={() => bulkMarkNotInHandRef.current?.closeModal()} />}
				/>
			</Modal>
			<Modal ref={mergeListingsRef}>
				<MergeListings handleClose={closeMergeListingsModal} />
			</Modal>
			<Modal ref={deleteListingModalRef}>
				<DeleteListing fetchInventory={fetchInventory} handleClose={closeDeleteListingsModal} />
			</Modal>
			<Modal ref={editPriceRef}>
				<CommonModal
					width={380}
					closeModal={() => editPriceRef.current?.closeModal()}
					title="Price Change Warning"
					content={<EditPriceModal cancel={cancelEditPriceModal} onConfirm={handleEditPriceModalConfirm} />}
				/>
			</Modal>
		</>
	);
};
